import { Channel } from 'api/ChannelsAPI';
import { UserContext } from 'core/userContext';
import { ReactNode } from 'react';
import styled from 'styled-components';
import { MessageMedium } from 'constants/messageMediums';
import { MessageType } from 'api/MessageTypeAPI';
import { SubscriptionPolicy } from 'modules/graphql';
import { BetaBadge } from 'ui/badge';
import { Delete, Edit16, Edit24, FrequencyCap, Migrate16, RateLimiting } from 'ui/icons';
import { ActionMenuOption, AllowedActionMenuElements } from 'ui/actionMenu/types';
import { spacing } from 'ui/theme';
import { CHANNEL_OVERFLOW_TEXT, MESSAGE_TYPES_OVERFLOW_TEXT } from '../constants';
import { isEmailMarketingChannel, isFrequencyCapChannelV2 } from './channels';

const BetaMenuOptionuWrapper = styled.div`
  display: flex;
  gap: ${spacing.half};
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const BetaText = ({ text }: { text: string }) => (
  <BetaMenuOptionuWrapper>
    {text}
    <BetaBadge />
  </BetaMenuOptionuWrapper>
);

type BaseOptionProps = {
  icon: ReactNode;
  isBeta?: boolean;
  text: string;
};

const BaseOption = ({ icon, isBeta, text }: BaseOptionProps) => (
  <>
    {icon}
    {isBeta ? <BetaText text={text} /> : <span>{text}</span>}
  </>
);

type CreateChannelOverflowOptions = {
  onDelete: () => void;
  onUpdate: () => void;
};

export const createChannelOverflowOptions = ({
  onDelete = () => undefined,
  onUpdate = () => undefined,
}: CreateChannelOverflowOptions) => {
  return [
    {
      as: AllowedActionMenuElements.Button,
      content: <BaseOption icon={<Edit16 />} text={CHANNEL_OVERFLOW_TEXT.UPDATE_LABEL} />,
      id: CHANNEL_OVERFLOW_TEXT.UPDATE_ID,
      isDisabled: false,
      onClick: onUpdate,
    },
    {
      as: AllowedActionMenuElements.Button,
      content: <BaseOption icon={<Delete />} text={CHANNEL_OVERFLOW_TEXT.DELETE_LABEL} />,
      id: CHANNEL_OVERFLOW_TEXT.DELETE_ID,
      isDisabled: false,
      onClick: onDelete,
    },
  ];
};

export type CreateMessageTypeOverflowOptions = {
  channel: Channel;
  messageType: MessageType;
  onDeleteMessageType: () => void;
  onIgnoreFrequencyCap: () => void;
  onUpdateFrequencyCap: () => void;
  onUpdateMessageType: () => void;
  onUpdateRateLimit: () => void;
  onUpdateToDoubleOptIn: () => void;
  featureFlags: UserContext['featureFlags'];
};

export const createMessageTypeOverflowOptions = ({
  channel,
  messageType,
  onDeleteMessageType = () => undefined,
  onIgnoreFrequencyCap = () => undefined,
  onUpdateFrequencyCap = () => undefined,
  onUpdateMessageType = () => undefined,
  onUpdateRateLimit,
  onUpdateToDoubleOptIn = () => undefined,
  featureFlags,
}: CreateMessageTypeOverflowOptions): ActionMenuOption[] => {
  const {
    CampaignRateLimiting,
    FrequencyCapping,
    FrequencyCappingV2,
    FrequencyCappingMessageType,
    MessageTypeRateLimiting,
    SMSDoubleOptIn,
  } = featureFlags;
  const isEmailMarketing = isEmailMarketingChannel(channel);
  const isSmsChannel = channel.messageMedium === MessageMedium.SMS;
  const isChannelEnabledV2 = isFrequencyCapChannelV2(channel);
  const hasFrequencyCapSet = !!channel?.frequencyManagement; // undefined or an object with properties

  const editOption = {
    as: AllowedActionMenuElements.Button,
    content: <BaseOption icon={<Edit24 />} text={MESSAGE_TYPES_OVERFLOW_TEXT.UPDATE_LABEL} />,
    id: MESSAGE_TYPES_OVERFLOW_TEXT.UPDATE_ID,
    onClick: onUpdateMessageType,
  };

  const rateLimitOption = {
    as: AllowedActionMenuElements.Button,
    content: (
      <BaseOption
        icon={<RateLimiting />}
        text={MESSAGE_TYPES_OVERFLOW_TEXT.RATE_LIMIT_LABEL}
        isBeta
      />
    ),
    id: MESSAGE_TYPES_OVERFLOW_TEXT.RATE_LIMIT_ID,
    onClick: onUpdateRateLimit,
  };

  const frequencyCapOption = {
    appendDivider: true,
    as: AllowedActionMenuElements.Button,
    content: (
      <BaseOption icon={<FrequencyCap />} text={MESSAGE_TYPES_OVERFLOW_TEXT.FREQUENCY_CAP_LABEL} />
    ),
    id: MESSAGE_TYPES_OVERFLOW_TEXT.FREQUENCY_CAP_ID,
    onClick: onUpdateFrequencyCap,
  };

  const ignoreFrequencyCapOption = {
    appendDivider: true,
    as: AllowedActionMenuElements.Button,
    content: (
      <BaseOption
        icon={<FrequencyCap />}
        text={MESSAGE_TYPES_OVERFLOW_TEXT.IGNORE_FREQUENCY_CAP_LABEL}
      />
    ),
    id: MESSAGE_TYPES_OVERFLOW_TEXT.IGNORE_FREQUENCY_CAP_ID,
    onClick: onIgnoreFrequencyCap,
  };

  const updateToDoubleOptInOption = {
    as: AllowedActionMenuElements.Button,
    content: (
      <BaseOption
        icon={<Migrate16 />}
        text={MESSAGE_TYPES_OVERFLOW_TEXT.UPDATE_DOUBLE_OPT_IN_LABEL}
      />
    ),
    id: MESSAGE_TYPES_OVERFLOW_TEXT.UPDATE_DOUBLE_OPT_IN_ID,
    onClick: onUpdateToDoubleOptIn,
  };

  const deleteOption = {
    as: AllowedActionMenuElements.Button,
    content: <BaseOption icon={<Delete />} text={MESSAGE_TYPES_OVERFLOW_TEXT.DELETE_LABEL} />,
    id: MESSAGE_TYPES_OVERFLOW_TEXT.DELETE_ID,
    onClick: onDeleteMessageType,
  };

  const filtered = [
    editOption,
    rateLimitOption,
    updateToDoubleOptInOption,
    frequencyCapOption,
    ignoreFrequencyCapOption,
    deleteOption,
  ].filter((option) => {
    // only show rate limit if FF is on
    if (option.id === MESSAGE_TYPES_OVERFLOW_TEXT.RATE_LIMIT_ID) {
      return !!CampaignRateLimiting || !!MessageTypeRateLimiting;
    }
    /**
     * Only show V1 (FrequencyCapping) if V2 (FrequencyCappingV2) and V3 (FrequencyCappingMessageType) are not enabled.
     */
    if (option.id === MESSAGE_TYPES_OVERFLOW_TEXT.FREQUENCY_CAP_ID) {
      return (
        !!FrequencyCapping &&
        !FrequencyCappingV2 &&
        !FrequencyCappingMessageType &&
        isEmailMarketing
      );
    }
    /**
     * If V2 (FrequencyCappingV2) is on, show "ignore frequency cap" option.
     * If V3 (FrequencyCappingMessageType) is on, hide settings here in favor of settings located at /settings/frequencyManagement.
     * */
    if (option.id === MESSAGE_TYPES_OVERFLOW_TEXT.IGNORE_FREQUENCY_CAP_ID) {
      return (
        !!FrequencyCappingV2 &&
        !FrequencyCappingMessageType &&
        isChannelEnabledV2 &&
        hasFrequencyCapSet
      );
    }

    if (option.id === MESSAGE_TYPES_OVERFLOW_TEXT.UPDATE_DOUBLE_OPT_IN_ID) {
      return (
        !!SMSDoubleOptIn &&
        isSmsChannel &&
        messageType.subscriptionPolicy === SubscriptionPolicy.OptIn
      );
    }

    return true;
  });

  return filtered;
};
